/**
 * 变量配置编辑器组件
 * 提供游戏变量的表单式编辑界面
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Tabs,
  Tag,
  Row,
  Col,
  Typography,
  Switch,
  message,
  Divider,
  Alert
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  DatabaseOutlined,
  CheckCircleOutlined
} from '@ant-design/icons'
import type { VariableConfigData } from '@/types/scriptConfigBlock'

const { TextArea } = Input
const { Text, Title } = Typography

// 变量类型选项
const VAR_TYPE_OPTIONS = [
  { value: 'boolean', label: '布尔值' },
  { value: 'integer', label: '整数' },
  { value: 'float', label: '浮点数' },
  { value: 'string', label: '字符串' },
  { value: 'list', label: '列表' },
]

// 变量作用域选项
const SCOPE_OPTIONS = [
  { value: 'global', label: '全局 (global)' },
  { value: 'player', label: '玩家 (player)' },
  { value: 'faction', label: '派系 (faction)' },
]

// 变量编辑器属性
interface VariableEditorFormProps {
  initialData?: VariableConfigData
  onSave: (data: VariableConfigData) => Promise<void>
  loading?: boolean
}

// 主组件
const VariableEditorForm: React.FC<VariableEditorFormProps> = ({
  initialData,
  onSave,
  loading = false
}) => {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('basic')
  const [saving, setSaving] = useState(false)

  // 初始化表单数据
  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        key: initialData.key,
        name: initialData.name,
        description: initialData.description,
        var_type: initialData.var_type,
        initial_value: initialData.initial_value,
        scope: initialData.scope,
        modifier: initialData.modifier,
        persist_between_sessions: initialData.persist_between_sessions,
      })
    } else {
      form.resetFields()
    }
  }, [initialData, form])

  // 保存处理
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      const variableData: VariableConfigData = {
        key: values.key,
        name: values.name,
        description: values.description,
        var_type: values.var_type,
        initial_value: values.initial_value,
        scope: values.scope,
        modifier: values.modifier,
        persist_between_sessions: values.persist_between_sessions,
      }

      await onSave(variableData)
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      title={
        <Space>
          <DatabaseOutlined style={{ color: '#fa8c16' }} />
          <span>{initialData?.name || '新建变量'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
          {initialData?.scope && <Tag>{initialData.scope}</Tag>}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleSave}
          loading={saving || loading}
        >
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="变量键" rules={[{ required: true }]}>
                  <Input placeholder="例如: var_evidence_count" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="变量名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 证据计数" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="scope" label="作用域" rules={[{ required: true }]}>
                  <Select placeholder="选择作用域" options={SCOPE_OPTIONS} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="description" label="描述">
                  <TextArea rows={2} placeholder="变量的用途说明..." />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="var_type" label="变量类型" rules={[{ required: true }]}>
                  <Select placeholder="选择类型" options={VAR_TYPE_OPTIONS} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="persist_between_sessions" label="跨局保持" valuePropName="checked">
                  <Switch checkedChildren="是" unCheckedChildren="否" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="initial_value" label="初始值">
                  <Input placeholder="根据类型而定" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="modifier" label="修饰符">
                  <Select
                    placeholder="选择修饰符（可选）"
                    allowClear
                    options={[
                      { value: 'sw_', label: '开关 (sw_)' },
                      { value: 'var_', label: '变量 (var_)' },
                      { value: 'rel_', label: '关系 (rel_)' },
                      { value: 'faction_', label: '派系 (faction_)' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="使用说明" key="usage">
            <Alert
              message="变量命名规范"
              description="建议使用统一的前缀来区分不同类型的变量："
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small" title="常用前缀">
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li><Text code>sw_</Text> - 开关变量 (true/false)</li>
                    <li><Text code>var_</Text> - 数值变量</li>
                    <li><Text code>rel_</Text> - 关系值变量</li>
                    <li><Text code>faction_</Text> - 派系声望变量</li>
                    <li><Text code>cnt_</Text> - 计数变量</li>
                  </ul>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="使用示例">
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li><Text code>sw_has_ticket</Text> - 是否有入场券</li>
                    <li><Text code>var_evidence_count</Text> - 证据数量</li>
                    <li><Text code>rel_npc_stranger</Text> - 与NPC关系</li>
                    <li><Text code>faction_hongmen</Text> - 洪门声望</li>
                  </ul>
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

const { TabPane } = Tabs

export default VariableEditorForm
